import * as React from "react";
import InterviewChat from "../modules/interview";
import {
  useGetCurrentSessionQuery,
  useGetSessionMessagesQuery,
  usePostSessionMessageMutation,
  useResetSessionMutation,
} from "../modules/interview/api/session-api";
import { Message } from "../modules/interview/conversation/area";
import { MessageRole } from "../modules/interview/api/api-models";

interface IInterviewPageProps {}

function createClientRequestId(): string {
  return crypto.randomUUID();
}

const RolesMap: { [key in MessageRole]: "user" | "assistant" | "refusal" | "pending" | "system" } = {
  "User": "user",
  "Assistant": "assistant",
  "Refusal": "refusal",
  "SystemHidden": "system"
};

const InterviewPage: React.FC<IInterviewPageProps> = (props) => {
  const [optimisticUserMessage, setOptimisticUserMessage] = React.useState<Message | null>(null);
  const [failedMessage, setFailedMessage] = React.useState<Message | null>(null);
  const [shouldPoll, setShouldPoll] = React.useState(false);

  const {
    data: currentSession,
    error: sessionError,
    isLoading: isSessionLoading,
    isError: isSessionError,
    refetch: refetchCurrentSession,
  } = useGetCurrentSessionQuery();

  const { data: messagesData, isLoading: isMessagesLoading } =
    useGetSessionMessagesQuery(
      { limit: 50 },
      { pollingInterval: shouldPoll ? 2000 : 0, skipPollingIfUnfocused: true },
    );

  const [postSessionMessage, { isLoading: isSending }] =
    usePostSessionMessageMutation();

  const [resetSession, { isLoading: isResetting }] = useResetSessionMutation();

  const canSend = currentSession?.limits.canSendMessage;

  const serverMessages = React.useMemo(
    () => messagesData?.items ?? [],
    [messagesData],
  );

  const serverSideProcessing = React.useMemo(
    () => serverMessages.some(
      (x) => x.processingStatus === "Pending" || x.processingStatus === "InProgress"
    ),
    [serverMessages],
  );

  React.useEffect(() => {
    setShouldPoll(serverSideProcessing);
  }, [serverSideProcessing]);

  const usedMessages = currentSession?.limits.sessionMessagesUsed ?? 0;
  const isProcessing = isSending || isResetting || serverSideProcessing;
  const lastMessage = serverMessages.length > 0 ? serverMessages[serverMessages.length - 1] : undefined;

  const showPendingDots = optimisticUserMessage !== null || serverSideProcessing;

  const handleSend = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;

    setOptimisticUserMessage({
      id: `optimistic-${Date.now()}`,
      type: "user",
      text: trimmed,
    });

    try {
      await postSessionMessage({
        clientRequestId: createClientRequestId(),
        text: trimmed,
      }).unwrap();
    } catch {
      setOptimisticUserMessage(null);
    }
  };

  React.useEffect(() => {
    setOptimisticUserMessage(null);
    if (serverMessages.at(-1)?.processingStatus === "Failed") {
      setFailedMessage({
        id: `failed-${Date.now()}`,
        type: "system",
        text: "Произошла ошибка при обработке сообщения. Пожалуйста, попробуйте отправить его снова.",
      });
    } else {
      setFailedMessage(null);
    }
  }, [serverMessages]);

  const handleReset = async () => {
    await resetSession({
      reason: "Нажали на кнопку Начать заново",
    }).unwrap();
  };

//   const handleRetryConnection = () => {
//     refetchCurrentSession();
//   };

  if (isSessionLoading || isMessagesLoading) {
    return <div>Загрузка interview...</div>;
  }

  return (
    <div className="size-full flex flex-col items-center overflow-hidden pb-1 px-4">
      <InterviewChat
        introData={
          isSessionError
            ? undefined
            : {
                title: "Пробное собеседование",
                description:
                  "Здесь можно заранее понять мой стиль общения, круг тем и профессиональный контекст.",
                questionsTitle: "Попробуйте спросить:",
                questions: [
                  "Расскажите про самый сложный production-инцидент, который вы расследовали",
                  "Как вы подходите к выбору архитектуры: монолит или микросервисы?",
                  "Какой у вас опыт менторинга и развития разработчиков в команде?",
                ],
              }
        }
        messages={[
          ...serverMessages.map((msg): Message => ({
            id: msg.messageId,
            text: msg.text,
            type: RolesMap[msg.role],
          })),
          ...(optimisticUserMessage ? [optimisticUserMessage] : []),
          ...(showPendingDots ? [{ id: "pending-dots", type: "pending" } as Message] : []),
          ...(failedMessage ? [failedMessage] : []),
        ]}
        usedMessages={usedMessages}
        isProcessing={isProcessing}
        lastMessageTime={
          lastMessage ? Date.parse(lastMessage.createdAtUtc) : undefined
        }
        totalMessages={currentSession?.limits.sessionMessageLimit ?? 20}
        warningThreshold={
          (currentSession?.limits.sessionMessageLimit ?? 20) * 0.8
        }
        sendMessage={handleSend}
        handleReset={handleReset}
        handleRetryConnection={refetchCurrentSession}
        isConnectionError={isSessionError}
        isSendDisabled={!canSend || isProcessing || isSessionError}

        totalTokens={currentSession?.limits.tokenBudgetLimit}
        usedTokens={currentSession?.limits.tokenBudgetUsed}
      />
    </div>
  );
};

export default InterviewPage;
